import { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { ArrowDownToLine, Check, Copy, ExternalLink, Search, ChevronDown, ChevronRight, FileText, Image as ImageIcon } from 'lucide-react';
import './branding.css';
import { OfficialPartners } from '../components/OfficialPartners';
import './docs-home.css';

const samples = {
  cURL: `curl -X POST "https://upload.bckt.io/v1/upload?folder=avatars" \\
  -H "Authorization: Bearer $BCKT_KEY" \\
  -H "Content-Type: image/webp" \\
  -H "X-File-Name: player-42.webp" \\
  --data-binary @player-42.webp`,
  JavaScript: `const file = await Bun.file("player-42.webp");

const response = await fetch(
  "https://upload.bckt.io/v1/upload?folder=avatars",
  {
    method: "POST",
    headers: {
      Authorization: \`Bearer \${process.env.BCKT_KEY}\`,
      "Content-Type": file.type,
      "X-File-Name": file.name,
    },
    body: file,
  },
);

console.log(await response.json());`,
  Python: `import os
import requests

with open("player-42.webp", "rb") as image:
    response = requests.post(
        "https://upload.bckt.io/v1/upload",
        params={"folder": "avatars"},
        headers={
            "Authorization": f"Bearer {os.environ['BCKT_KEY']}",
            "Content-Type": "image/webp",
            "X-File-Name": "player-42.webp",
        },
        data=image,
    )

print(response.json())`,
  Lua: `local response = PerformHttpRequestAwait(
  "https://api.bckt.io/api/v1/logs/ingest/production",
  "POST",
  json.encode({
    level = "info",
    event_type = "player.connected",
    entity_id = source,
    payload = { endpoint = GetPlayerEndpoint(source) }
  }),
  { ["Authorization"] = "Bearer " .. GetConvar("bckt_key", "") }
)`,
};

function CodeBlock({ code }: { code: string }) {
  const [copied, setCopied] = useState(false);
  return (
    <div className="code-block">
      <button
        className={copied ? 'is-success' : ''}
        onClick={() => {
          void navigator.clipboard.writeText(code);
          setCopied(true);
          window.setTimeout(() => setCopied(false), 1200);
        }}
      >
        {copied ? <Check /> : <Copy />}
        {copied ? 'Copied' : 'Copy'}
      </button>
      <pre>
        <code>{code}</code>
      </pre>
    </div>
  );
}
function InlineCodeBlock({ code }: { code: string }) {
  const [copied, setCopied] = useState(false);

  // Clean code tokenization for Lua / JS lines
  const renderHighlighted = (raw: string) => {
    // Regex matching comments, strings, keywords, numbers, identifiers
    const tokenRegex = /(--.*$|\/\/.*$)|('(?:\\.|[^'\\])*'|"(?:\\.|[^"\\])*")|\b(local|<const>|const|let|var|function|return|end|endpoint)\b|\b([0-9]+)\b|([a-zA-Z_][a-zA-Z0-9_]*)|([=,<>{}()[\]:;])/g;
    const elements: React.ReactNode[] = [];
    let lastIndex = 0;
    let match: RegExpExecArray | null;

    while ((match = tokenRegex.exec(raw)) !== null) {
      if (match.index > lastIndex) {
        elements.push(raw.slice(lastIndex, match.index));
      }
      const [token, comment, str, keyword, num, ident, punct] = match;
      if (comment) {
        elements.push(<span key={match.index} style={{ color: "var(--color-muted)" }}>{token}</span>);
      } else if (str) {
        elements.push(<span key={match.index} style={{ color: "#7ee787" }}>{token}</span>);
      } else if (keyword) {
        elements.push(<span key={match.index} style={{ color: "#ff7b72", fontWeight: 500 }}>{token}</span>);
      } else if (num) {
        elements.push(<span key={match.index} style={{ color: "#79c0ff" }}>{token}</span>);
      } else if (ident) {
        // Variable / property
        elements.push(<span key={match.index} style={{ color: "#d2a8ff" }}>{token}</span>);
      } else if (punct) {
        elements.push(<span key={match.index} style={{ color: "var(--color-ink-2)" }}>{token}</span>);
      } else {
        elements.push(token);
      }
      lastIndex = tokenRegex.lastIndex;
    }
    if (lastIndex < raw.length) {
      elements.push(raw.slice(lastIndex));
    }
    return elements;
  };

  return (
    <div style={{
      position: 'relative',
      background: 'var(--color-paper-3)',
      border: 'var(--rule-hair) solid var(--color-rule)',
      borderRadius: 'var(--radius-sm)',
      padding: '8px 12px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      gap: '12px',
      overflowX: 'auto'
    }}>
      <pre style={{ margin: 0, padding: 0, minWidth: 0, fontSize: '12px', lineHeight: '1.45', color: 'var(--color-ink)' }}>
        <code style={{ fontFamily: 'var(--font-mono)' }}>{renderHighlighted(code)}</code>
      </pre>
      <button
        type="button"
        onClick={() => {
          void navigator.clipboard.writeText(code);
          setCopied(true);
          window.setTimeout(() => setCopied(false), 1200);
        }}
        style={{
          background: copied ? 'color-mix(in oklch, var(--color-success) 15%, transparent)' : 'var(--color-paper-2)',
          border: 'var(--rule-hair) solid var(--color-rule)',
          borderRadius: '4px',
          color: copied ? 'var(--color-success)' : 'var(--color-muted)',
          fontSize: '11px',
          padding: '3px 8px',
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          gap: '5px',
          whiteSpace: 'nowrap',
          flexShrink: 0
        }}
      >
        {copied ? <Check style={{ width: '12px', height: '12px' }} /> : <Copy style={{ width: '12px', height: '12px' }} />}
        {copied ? 'Copied' : 'Copy'}
      </button>
    </div>
  );
}

interface ScriptGuide {
  id: string;
  name: string;
  author: string;
  type: "logs" | "media" | "both";
  description: string;
  sections: {
    title: string;
    kind: "logs" | "media" | "both";
    file: string;
    lineRef?: string;
    instructions: string;
    before: string;
    after: string;
  }[];
}

const FAMOUS_SCRIPTS: ScriptGuide[] = [
  {
    id: "ox_lib",
    name: "ox_lib",
    author: "Overextended",
    type: "logs",
    description: "High-performance standalone utility library with structured batch logging support.",
    sections: [
      {
        title: "Batch Logging Endpoint",
        kind: "logs",
        file: "ox_lib/imports/logger/providers/fivemanage.lua",
        lineRef: "Line 30",
        instructions: "Change the endpoint URL to api.bckt.io.",
        before: "endpoint = 'https://api.fivemanage.com/api/logs/batch',",
        after: "endpoint = 'https://api.bckt.io/api/logs/batch',",
      },
    ],
  },
  {
    id: "oxmysql",
    name: "oxmysql",
    author: "Overextended",
    type: "logs",
    description: "MySQL / MariaDB database driver with automated query telemetry and slow query tracking.",
    sections: [
      {
        title: "Database Telemetry Endpoint",
        kind: "logs",
        file: "oxmysql/logger/fivemanage.js",
        lineRef: "Line 8",
        instructions: "Point the endpoint constant to api.bckt.io.",
        before: "const endpoint = 'https://api.fivemanage.com/api/logs/batch';",
        after: "const endpoint = 'https://api.bckt.io/api/logs/batch';",
      },
    ],
  },
  {
    id: "sd-phone",
    name: "sd-phone",
    author: "Samuel's Development",
    type: "media",
    description: "Realistic smartphone resource with camera photos, photo galleries, and video clips.",
    sections: [
      {
        title: "Direct Client Presigned Uploads",
        kind: "media",
        file: "sd-phone/server/photos/presign.lua",
        lineRef: "Line 26",
        instructions: "Update PRESIGN_URL to BCKT presigned upload router.",
        before: "local PRESIGN_URL <const> = 'https://api.fivemanage.com/api/v3/file/presigned-url'",
        after: "local PRESIGN_URL <const> = 'https://api.bckt.io/api/v3/file/presigned-url'",
      },
      {
        title: "Base64 Photo Upload Fallback",
        kind: "media",
        file: "sd-phone/server/photos/uploader.lua",
        lineRef: "Line 19",
        instructions: "Replace the base64 upload URL with BCKT.",
        before: "local UPLOAD_URL = 'https://api.fivemanage.com/api/v3/file/base64'",
        after: "local UPLOAD_URL = 'https://api.bckt.io/api/v3/file/base64'",
      },
    ],
  },
  {
    id: "sky_phone",
    name: "sky_phone",
    author: "Sky-Systems",
    type: "media",
    description: "Advanced smartphone resource with gallery, camera, and in-game media synchronization.",
    sections: [
      {
        title: "Media host allowlist", kind: "media",
        file: "sky_phone/source/server/media.lua", lineRef: "Line 8",
        instructions: "Replace the allowed media host with api.bckt.io.",
        before: '["api.fivemanage.com"] = true,', after: '["api.bckt.io"] = true,',
      },
      {
        title: "Fivemanage Media Base URL",
        kind: "media",
        file: "sky_phone/source/shared/config_default.lua",
        lineRef: "Line 1574",
        instructions: "Update the BaseUrl property in FiveManage config block to BCKT API.",
        before: 'BaseUrl = "https://api.fivemanage.com/api/v3/file",',
        after: 'BaseUrl = "https://api.bckt.io/api/v3/file",',
      },
      {
        title: "Media base URL (shared default)", kind: "media",
        file: "sky_phone/config/media.lua", lineRef: "Line 24",
        instructions: "Point BaseUrl at the BCKT media-compatible endpoint.",
        before: 'BaseUrl = "https://api.fivemanage.com/api/v3/file",',
        after: 'BaseUrl = "https://api.bckt.io/api/v3/file",',
      },
      {
        title: "Media base URL (test server)", kind: "media",
        file: "frontend/testserver/index.cjs", lineRef: "Line 4889",
        instructions: "Update the test fixture URL when validating uploads locally.",
        before: "value: 'https://api.fivemanage.com/api/v3/file',",
        after: "value: 'https://api.bckt.io/api/v3/file',",
      },
      {
        title: "Media host contract test", kind: "media",
        file: "frontend/src/mediaConfig.server.contract.test.ts", lineRef: "Line 85",
        instructions: "Update the expected allowed host in the contract test.",
        before: 'expect(mediaServer).toContain(\'["api.fivemanage.com"] = true\')',
        after: 'expect(mediaServer).toContain(\'["api.bckt.io"] = true\')',
      },
    ],
  },
  {
    id: "ps-mdt",
    name: "ps-mdt",
    author: "Project Sloth",
    type: "both",
    description: "Mobile Data Terminal for emergency services with incident photos and audit event logging.",
    sections: [
      {
        title: "Incident Media & Audit Logging Endpoints",
        kind: "both",
        file: "server/fivemanage.lua",
        lineRef: "Line 9",
        instructions: "Point the image upload URL and audit logging endpoint to BCKT.",
        before: "local FiveManageApiUrl = 'https://api.fivemanage.com/api/v3/file/base64'\nlocal FiveManageLogsUrl = 'https://api.fivemanage.com/api/v3/logs'",
        after: "local FiveManageApiUrl = 'https://api.bckt.io/api/v3/file/base64'\nlocal FiveManageLogsUrl = 'https://api.bckt.io/api/v3/logs'",
      },
    ],
  },
  {
    id: "qb-smallresources",
    name: "qb-smallresources",
    author: "QBcore",
    type: "logs",
    description: "Common QBCore resources with server event logging through the Fivemanage endpoint.",
    sections: [{
      title: "Server logging endpoint", kind: "logs",
      file: "server/logs.lua", lineRef: "Line 113",
      instructions: "Point PerformHttpRequest at the BCKT logs-compatible endpoint.",
      before: "PerformHttpRequest('https://api.fivemanage.com/api/logs', function(statusCode, response, headers)",
      after: "PerformHttpRequest('https://api.bckt.io/api/logs', function(statusCode, response, headers)",
    }],
  },
];

function ScriptsIntegrationDirectory() {
  const [search, setSearch] = useState("");
  const [selectedKind, setSelectedKind] = useState<"all" | "logs" | "media">("all");
  const [openIds, setOpenIds] = useState<string[]>([]);

  const toggle = (id: string) => {
    setOpenIds((prev) => (prev.includes(id) ? prev.filter((item) => item !== id) : [...prev, id]));
  };

  const filtered = FAMOUS_SCRIPTS.filter((script) => {
    const matchesSearch =
      script.name.toLowerCase().includes(search.toLowerCase()) ||
      script.author.toLowerCase().includes(search.toLowerCase()) ||
      script.description.toLowerCase().includes(search.toLowerCase());
    const matchesKind =
      selectedKind === "all" || script.type === selectedKind || script.type === "both";
    return matchesSearch && matchesKind;
  });

  return (
    <div className="scripts-directory" style={{ marginTop: "24px", display: "flex", flexDirection: "column", gap: "16px" }}>
      <div style={{ display: "flex", gap: "10px", flexWrap: "wrap", alignItems: "center" }}>
        <div
          className="dash-search"
          style={{
            flex: "1 1 240px",
            height: "38px",
            border: "var(--rule-hair) solid var(--color-rule)",
            background: "var(--color-paper-2)",
            borderRadius: "var(--radius-sm)",
            padding: "0 12px",
            display: "flex",
            alignItems: "center",
            gap: "8px",
          }}
        >
          <Search style={{ width: "15px", height: "15px", color: "var(--color-muted)" }} />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search script by name, author or resource..."
            style={{
              background: "transparent",
              border: "none",
              outline: "none",
              color: "var(--color-ink)",
              fontSize: "13px",
              width: "100%",
            }}
          />
        </div>

        <div style={{ display: "flex", gap: "4px", background: "var(--color-paper-2)", padding: "3px", borderRadius: "var(--radius-sm)", border: "var(--rule-hair) solid var(--color-rule)" }}>
          {(
            [
              { id: "all", label: "All Scripts" },
              { id: "logs", label: "Logs Only" },
              { id: "media", label: "Media Only" },
            ] as const
          ).map((filter) => (
            <button
              key={filter.id}
              type="button"
              onClick={() => setSelectedKind(filter.id)}
              style={{
                padding: "4px 12px",
                fontSize: "12px",
                fontWeight: 500,
                borderRadius: "4px",
                border: "none",
                cursor: "pointer",
                background: selectedKind === filter.id ? "color-mix(in oklch, var(--color-ink) 10%, transparent)" : "transparent",
                color: selectedKind === filter.id ? "var(--color-ink)" : "var(--color-muted)",
                transition: "all var(--dur-fast) ease",
              }}
            >
              {filter.label}
            </button>
          ))}
        </div>
      </div>

      <div style={{ display: "flex", flexDirection: "column", gap: "10px" }}>
        {filtered.map((script) => {
          const isOpen = openIds.includes(script.id);
          return (
            <div
              key={script.id}
              style={{
                border: "var(--rule-hair) solid var(--color-rule)",
                borderRadius: "var(--radius-sm)",
                background: "var(--color-paper-2)",
                overflow: "hidden",
                transition: "border-color 0.15s ease",
              }}
            >
              <button
                type="button"
                onClick={() => toggle(script.id)}
                style={{
                  width: "100%",
                  padding: "14px 18px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  background: "transparent",
                  border: "none",
                  cursor: "pointer",
                  textAlign: "left",
                }}
              >
                <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
                  <span style={{ color: "var(--color-accent)" }}>
                    {isOpen ? <ChevronDown style={{ width: "16px", height: "16px" }} /> : <ChevronRight style={{ width: "16px", height: "16px" }} />}
                  </span>
                  <div>
                    <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                      <strong style={{ fontSize: "15px", color: "var(--color-ink)" }}>{script.name}</strong>
                      <span style={{ fontSize: "12px", color: "var(--color-muted)" }}>({script.author})</span>
                      <span
                        style={{
                          fontSize: "10px",
                          fontWeight: 600,
                          textTransform: "uppercase",
                          letterSpacing: "0.04em",
                          padding: "2px 6px",
                          borderRadius: "4px",
                          background: script.type === "logs" ? "color-mix(in oklch, var(--color-info) 18%, transparent)" : script.type === "media" ? "color-mix(in oklch, var(--color-success) 18%, transparent)" : "color-mix(in oklch, var(--color-accent) 18%, transparent)",
                          color: script.type === "logs" ? "var(--color-info)" : script.type === "media" ? "var(--color-success)" : "var(--color-accent)",
                        }}
                      >
                        {script.type}
                      </span>
                    </div>
                    <p style={{ margin: "3px 0 0", fontSize: "12px", color: "var(--color-muted)" }}>{script.description}</p>
                  </div>
                </div>
              </button>

              {isOpen && (
                <div
                  style={{
                    padding: "0 18px 18px",
                    borderTop: "var(--rule-hair) solid var(--color-rule)",
                    background: "var(--color-paper)",
                    display: "flex",
                    flexDirection: "column",
                    gap: "16px",
                  }}
                >
                  {script.sections.map((sec, idx) => (
                    <div 
                      key={idx} 
                      style={{ 
                        paddingTop: idx > 0 ? "16px" : "14px",
                        marginTop: idx > 0 ? "16px" : "0",
                        borderTop: idx > 0 ? "var(--rule-hair) solid var(--color-rule)" : "none"
                      }}
                    >
                      <div style={{ display: "flex", alignItems: "center", gap: "8px", marginBottom: "12px" }}>
                        <code style={{ fontSize: "12px", color: "var(--color-ink)", background: "var(--color-paper-2)", padding: "3px 8px", borderRadius: "4px", border: "var(--rule-hair) solid var(--color-rule)", fontFamily: "var(--font-mono)" }}>
                          {sec.file}
                        </code>
                        {sec.lineRef && (
                          <span style={{ fontSize: "12px", color: "var(--color-muted)", fontFamily: "var(--font-mono)" }}>
                            ({sec.lineRef})
                          </span>
                        )}
                      </div>

                      <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
                        <span style={{ fontSize: "11px", fontWeight: 500, color: "var(--color-muted)", textTransform: "uppercase", letterSpacing: "0.05em" }}>
                          Change:
                        </span>
                        <InlineCodeBlock code={sec.before} />

                        <span style={{ fontSize: "11px", fontWeight: 500, color: "var(--color-muted)", textTransform: "uppercase", letterSpacing: "0.05em", marginTop: "2px" }}>
                          for:
                        </span>
                        <InlineCodeBlock code={sec.after} />
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

export function Docs() {
  const [language, setLanguage] = useState<keyof typeof samples>('cURL');
  const location = useLocation();
  const [view, setView] = useState<'home' | 'docs' | 'scripts' | 'migration'>(() => {
    return !location.hash || location.hash === '#home' ? 'home' : location.hash === '#scripts' ? 'scripts' : location.hash === '#fivemanage' ? 'migration' : 'docs';
  });

  useEffect(() => {
    const handleHash = () => {
      if (!window.location.hash || window.location.hash === '#home') {
        setView('home');
      } else if (window.location.hash === '#fivemanage') {
        setView('migration');
      } else if (window.location.hash === '#scripts') {
        setView('scripts');
      } else {
        setView('docs');
      }
    };
    handleHash();
    window.addEventListener('hashchange', handleHash);
    return () => window.removeEventListener('hashchange', handleHash);
  }, [location]);

  useEffect(() => {
    if (view !== 'migration' && view !== 'scripts') return;
    const frame = requestAnimationFrame(() => {
      const target = document.getElementById(view === 'migration' ? 'fivemanage' : 'scripts');
      target?.scrollIntoView({ behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'instant' : 'smooth', block: 'start' });
    });
    return () => cancelAnimationFrame(frame);
  }, [view]);

  return (
    <div className="docs-page shell">
      <aside className="docs-nav">
        <strong>Start here</strong>
        <a href="#home" className={view === 'home' ? 'is-active' : ''} onClick={() => setView('home')}>Welcome</a>
        <a 
          href="#quickstart" 
          className={view === 'docs' && (!window.location.hash || window.location.hash === '#quickstart') ? 'is-active' : ''}
          onClick={() => setView('docs')}
        >
          Quickstart
        </a>
        <a 
          href="#authentication" 
          className={view === 'docs' && window.location.hash === '#authentication' ? 'is-active' : ''}
          onClick={() => setView('docs')}
        >
          Authentication
        </a>
        <a 
          href="#uploads" 
          className={view === 'docs' && window.location.hash === '#uploads' ? 'is-active' : ''}
          onClick={() => setView('docs')}
        >
          Uploads
        </a>
        <a 
          href="#presigned" 
          className={view === 'docs' && window.location.hash === '#presigned' ? 'is-active' : ''}
          onClick={() => setView('docs')}
        >
          Upload URLs
        </a>

        <strong>Fivemanage Migration</strong>
        <a 
          href="#fivemanage" 
          className={view === 'migration' ? 'is-active' : ''}
          onClick={() => setView('migration')}
        >
          Compatibility & endpoints
        </a>
        <a href="#scripts" className={view === 'scripts' ? 'is-active' : ''} onClick={() => { setView('scripts'); window.location.hash = '#scripts'; }}>
          Install bckt-hook
        </a>

        <strong>Storage</strong>
        <a 
          href="#uploads" 
          className={view === 'docs' && window.location.hash === '#uploads' ? 'is-active' : ''}
          onClick={() => setView('docs')}
        >
          File catalog
        </a>
        <a 
          href="#s3" 
          className={view === 'docs' && window.location.hash === '#s3' ? 'is-active' : ''}
          onClick={() => setView('docs')}
        >
          S3 gateway
        </a>

        <strong>Logs</strong>
        <a 
          href="#ingest" 
          className={view === 'docs' && window.location.hash === '#ingest' ? 'is-active' : ''}
          onClick={() => setView('docs')}
        >
          Ingest events
        </a>
        <a 
          href="#query" 
          className={view === 'docs' && window.location.hash === '#query' ? 'is-active' : ''}
          onClick={() => setView('docs')}
        >
          Search logs
        </a>

        <strong>Reference</strong>
        <a href="/api/docs">
          OpenAPI reference <ExternalLink />
        </a>
      </aside>

      {view === 'home' ? (
        <article className="docs-content docs-home">
          <header className="docs-home__hero" id="home">
            <h1 className="docs-home__banner"><img src="/docs/welcome-v3.webp" width="1240" height="400" alt="BCKT Documentation. Welcome to building with BCKT. Object storage. Edge delivery. Logs." fetchPriority="high" /></h1>
            <p>Store files, deliver media, and make sense of your logs. Find the guides and API references to connect your application to BCKT.</p>
            <a className="button button--light" href="#quickstart" onClick={() => setView('docs')}>Make your first upload <ChevronRight /></a>
          </header>
          <section className="docs-home__guides">
            <h2>What are you building?</h2>
            <div className="docs-home__links">
              {[
                ['uploads', 'Upload & deliver', 'Send files to BCKT and serve them through the CDN.'],
                ['s3', 'Connect an S3 client', 'Use your existing tools with the BCKT S3 gateway.'],
                ['ingest', 'Collect application logs', 'Send structured events and search your log streams.'],
                ['scripts', 'Migrate from Fivemanage', 'Start with the beta Lua hook or integrate the API directly.'],
              ].map(([id, title, copy]) => (
                <a key={id} href={`#${id}`} onClick={() => setView(id === 'scripts' ? 'scripts' : 'docs')}><strong>{title}<ChevronRight /></strong><span>{copy}</span></a>
              ))}
            </div>
          </section>
          <section className="docs-home__essentials">
            <h2>Before you start</h2>
            <p>Create your credentials in <a href="/dashboard/credentials">API and S3</a>. Keep secret keys on your server; use <a href="#presigned" onClick={() => setView('docs')}>upload URLs</a> for client-side uploads.</p>
            <a className="text-link" href="/api/docs">Explore the API reference <ExternalLink /></a>
          </section>
          <OfficialPartners />
        </article>
      ) : view === 'migration' ? (
        <article className="docs-content"><header><p>Fivemanage migration</p><h1>Connect existing integrations.</h1><p>Compatibility routes for scripts that already use Fivemanage. For the native BCKT API, start with the Quickstart guide.</p></header>
          <section id="fivemanage">
            <h2>Migrating from Fivemanage</h2>
            <p>
              No need to change your logic. BCKT has dedicated endpoints compatible with Fivemanage V3
              Media and Logs. Keep the same paths, JSON structure and Authorization header. Point the host at{' '}
              <code>https://api.bckt.io</code> and use your standard BCKT API key.
            </p>
            <CodeBlock
              code={`# before
https://api.fivemanage.com/api/v3/file

# after
https://api.bckt.io/api/v3/file`}
            />
            <p>
              Compatible media routes: list, multipart upload, base64 upload, presigned URL, metadata and
              delete. File URLs are served lightning-fast from <code>cdn.bckt.io</code>.
            </p>
            <p>
              Compatible logs routes: <code>/v3/logs</code> (POST ingest & GET query), <code>/v3/logs/:traceId</code> (GET), and legacy batch <code>/api/logs/batch</code>.
            </p>
            <div className="callout" style={{ marginTop: '18px' }}>
              <strong>Are you a server owner?</strong>
              <p>Try <code>bckt-hook</code> to redirect supported Lua requests to BCKT. The hook is in beta and may not work with every resource. C# and JavaScript scripts are not supported.</p>
              <a className="btn" href="#scripts" onClick={() => { setView('scripts'); window.location.hash = '#scripts'; }}>Open the bckt-hook guide →</a>
              <a className="btn" href="/downloads/bckt-hook.zip" download style={{ marginLeft: '8px' }}>Download bckt-hook.zip</a>
            </div>
          </section>
        </article>
      ) : view === 'scripts' ? (
        <article id="scripts" className="docs-content">
          <header>
            <p>Fivemanage compatibility</p>
            <h1>Install bckt-hook once.</h1>
            <p>
              bckt-hook redirects supported server-side Lua requests from api.fivemanage.com to api.bckt.io.
            </p>
          </header>
          <section id="hook-install">
            <div className="callout">
              <strong>Beta · Lua only</strong>
              <p>This hook is in beta and may not work with certain Lua scripts, including FXAP-protected resources. It does not intercept requests made by C# or JavaScript scripts.</p>
              <p>If your script supports Fivemanage but does not support BCKT, ask its author to add BCKT as a supported service. For a compatible integration, adding the BCKT endpoint is a simple change that can take about a minute; validation may take longer.</p>
            </div>
            <h2 style={{ marginTop: '24px' }}>Installation</h2>
            <p>Copy the <code>bckt-hook</code> resource into your server resources directory, then add it before every resource that should be covered.</p>
            <CodeBlock code={`ensure bckt-hook`} />
            <div style={{ marginTop: '16px', marginBottom: '18px' }}>
              <a className="branding-download" href="/downloads/bckt-hook.zip" download>
                <ArrowDownToLine size={16} aria-hidden="true" /> Download bckt-hook.zip
              </a>
            </div>
            <p>In the target resource's <code>fxmanifest.lua</code>, add this line at the very beginning:</p>
            <CodeBlock code={`server_script '@bckt-hook/hook.lua'`} />
            <div className="callout">
              <strong>Set your BCKT API key</strong>
              <p>Enter your BCKT API key in the resource's server-side Fivemanage API key setting, then restart the server and test the integration.</p>
            </div>
          </section>
        </article>
      ) : (
        <article className="docs-content">
          <header>
            <p>Documentation</p>
            <h1>Build with ordinary tools.</h1>
            <p>
              BCKT uses HTTP, JSON, and standard S3 credentials. Upload a file, retain an event, or
              connect an existing backup client without learning a proprietary protocol.
            </p>
          </header>
          <section id="quickstart">
            <h2>Quickstart</h2>
            <p>
              Create an API key in the dashboard, then send a file to the direct upload endpoint. The
              request body streams into private object storage and the response contains its CDN URL.
            </p>
            <div className="code-tabs" role="tablist">
              {Object.keys(samples).map((item) => (
                <button
                  role="tab"
                  aria-selected={language === item}
                  className={language === item ? 'is-active' : ''}
                  onClick={() => setLanguage(item as keyof typeof samples)}
                  key={item}
                >
                  {item}
                </button>
              ))}
            </div>
            <CodeBlock code={samples[language]} />
            <div className="response-example">
              <span>201 Created</span>
              <CodeBlock
                code={`{
  "success": true,
  "url": "https://cdn.bckt.io/a8K2mP9q.webp",
  "file_key": "a8K2mP9q.webp",
  "size": 84211
}`}
              />
            </div>
          </section>
          <section id="authentication">
            <h2>Authentication</h2>
            <p>
              REST requests use a Bearer token. API keys begin with <code>bckt_</code> and can be
              scoped independently for file reads, file writes, log reads, and log writes.
            </p>
            <div className="callout">
              <strong>Keep keys on the server.</strong>
              <p>
                Do not expose a full-access API key in browser code. Customer dashboard sessions use
                secure HTTP-only cookies.
              </p>
            </div>
          </section>
          <section id="uploads">
            <h2>Streaming uploads</h2>
            <p>
              Send binary bodies or a multipart form to{' '}
              <code>POST https://upload.bckt.io/v1/upload</code>. Files can be up to 20 GB with chunked multipart (up to 100 MB on single upload). Use{' '}
              <code>?folder=clips/2026</code> for a virtual folder, and <code>X-File-Name</code> when
              sending a binary body. Add <code>?private=true</code> to create a private object.
              Private objects require a one-hour signed link generated from the dashboard or file API.
            </p>
            <table>
              <thead>
                <tr>
                  <th>Header</th>
                  <th>Required</th>
                  <th>Purpose</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>Authorization</td>
                  <td>Yes</td>
                  <td>BCKT session token or API key</td>
                </tr>
                <tr>
                  <td>Content-Length</td>
                  <td>Yes</td>
                  <td>Preflight quota enforcement</td>
                </tr>
                <tr>
                  <td>Content-Type</td>
                  <td>Recommended</td>
                  <td>CDN response content type</td>
                </tr>
                <tr>
                  <td>X-File-Name</td>
                  <td>Binary only</td>
                  <td>Human-readable original name</td>
                </tr>
                <tr>
                  <td>X-Content-SHA256</td>
                  <td>No</td>
                  <td>Early blacklist check and integrity validation</td>
                </tr>
              </tbody>
            </table>
          </section>
          <section id="presigned">
            <h2>Upload URLs</h2>
            <p>
              Request a short-lived URL from the BCKT API, then send the file body to that host. REST
              URLs expire in 15 minutes and land on <code>upload.bckt.io</code>. S3 URLs land on{' '}
              <code>s3.bckt.io</code>. The browser never needs the API key.
            </p>
            <CodeBlock
              code={`curl -X POST https://api.bckt.io/api/v1/files/upload-url \
  -H "Authorization: Bearer $BCKT_KEY" \
  -H "Content-Type: application/json" \
  -d '{"filename":"clip.webm","folder":"clips","content_type":"video/webm","size":84211,"private":false}'

# then
curl -X POST "$UPLOAD_URL" \
  -H "Content-Type: video/webm" \
  -H "Content-Length: 84211" \
  -H "X-File-Name: clip.webm" \
  --data-binary @clip.webm`}
            />
            <p>
              For S3 clients, request <code>POST /files/s3-url</code> with an object key. The response
              is a SigV4 query URL for the <code>bckt</code> bucket. PUT uses the same path-style
              gateway as ordinary S3 credentials.
            </p>
          </section>
          <section id="s3">
            <h2>S3 gateway</h2>
            <p>
              Create S3 credentials in the dashboard, then point any path-style S3 client at{' '}
              <code>https://s3.bckt.io</code>. The gateway exposes one logical bucket named{' '}
              <code>bckt</code>. BCKT signs and stores the object under your tenant without exposing
              the origin provider.
            </p>
            <CodeBlock
              code={`[bckt]
type = s3
provider = Other
access_key_id = BCKTXXXXXXXXXXXXXXXX
secret_access_key = your-generated-secret
endpoint = https://s3.bckt.io
region = auto
force_path_style = true`}
            />
            <p>
              The gateway supports bucket discovery, object PUT, GET, DELETE, and ListObjects V1/V2.
              Single-object uploads can be up to 20 GB. Presigned query URLs are accepted on the same host.
            </p>
          </section>
          <section id="ingest">
            <h2>Ingest logs</h2>
            <p>
              Send one JSON event or an array of up to 1,000 events. <code>timestamp</code>,{' '}
              <code>level</code>, <code>event_type</code>, and <code>entity_id</code> are indexed
              fields. Everything else belongs in <code>payload</code>.
            </p>
            <CodeBlock
              code={`{
  "timestamp": "2026-08-30T14:42:08.219Z",
  "level": "info",
  "event_type": "player.connected",
  "entity_id": "char:88219",
  "message": "Session restored in 82ms",
  "payload": { "server": "eu-2", "latency_ms": 82 }
}`}
            />
          </section>
          <section id="query">
            <h2>Search logs</h2>
            <p>
              Queries require a stream and an ISO 8601 time range. Optional filters cover event type,
              entity ID, and free-text search across the message and JSON payload. One request can
              span up to 31 days.
            </p>
            <CodeBlock
              code={`curl -G https://api.bckt.io/api/v1/logs/query \
  -H "Authorization: Bearer $BCKT_KEY" \
  --data-urlencode "stream=production" \
  --data-urlencode "from=2026-08-30T00:00:00Z" \
  --data-urlencode "to=2026-08-30T23:59:59Z" \
  --data-urlencode "entity_id=char:88219"`}
            />
          </section>
        </article>
      )}
    </div>
  );
}
